import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Users, CreditCard, Loader2, Phone, Mail, MessageCircle, Landmark } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { Calendar as CalendarComponent } from '../components/ui/calendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'GR', name: 'Greece', nameEl: 'Ελλάδα', dial: '+30' },
  { code: 'GB', name: 'United Kingdom', nameEl: 'Ηνωμένο Βασίλειο', dial: '+44' },
  { code: 'DE', name: 'Germany', nameEl: 'Γερμανία', dial: '+49' },
  { code: 'RS', name: 'Serbia', nameEl: 'Σερβία', dial: '+381' },
  { code: 'RO', name: 'Romania', nameEl: 'Ρουμανία', dial: '+40' },
  { code: 'BG', name: 'Bulgaria', nameEl: 'Βουλγαρία', dial: '+359' },
  { code: 'MK', name: 'North Macedonia', nameEl: 'Βόρεια Μακεδονία', dial: '+389' },
  { code: 'IL', name: 'Israel', nameEl: 'Ισραήλ', dial: '+972' },
  { code: 'US', name: 'United States', nameEl: 'ΗΠΑ', dial: '+1' },
  { code: 'FR', name: 'France', nameEl: 'Γαλλία', dial: '+33' },
  { code: 'IT', name: 'Italy', nameEl: 'Ιταλία', dial: '+39' },
  { code: 'ES', name: 'Spain', nameEl: 'Ισπανία', dial: '+34' },
  { code: 'NL', name: 'Netherlands', nameEl: 'Ολλανδία', dial: '+31' },
  { code: 'AT', name: 'Austria', nameEl: 'Αυστρία', dial: '+43' },
  { code: 'CH', name: 'Switzerland', nameEl: 'Ελβετία', dial: '+41' },
  { code: 'CY', name: 'Cyprus', nameEl: 'Κύπρος', dial: '+357' },
  { code: 'TR', name: 'Turkey', nameEl: 'Τουρκία', dial: '+90' },
  { code: 'PL', name: 'Poland', nameEl: 'Πολωνία', dial: '+48' },
  { code: 'SE', name: 'Sweden', nameEl: 'Σουηδία', dial: '+46' },
  { code: 'NO', name: 'Norway', nameEl: 'Νορβηγία', dial: '+47' },
  { code: 'DK', name: 'Denmark', nameEl: 'Δανία', dial: '+45' },
  { code: 'FI', name: 'Finland', nameEl: 'Φινλανδία', dial: '+358' },
  { code: 'PT', name: 'Portugal', nameEl: 'Πορτογαλία', dial: '+351' },
  { code: 'BE', name: 'Belgium', nameEl: 'Βέλγιο', dial: '+32' },
  { code: 'CZ', name: 'Czech Republic', nameEl: 'Τσεχία', dial: '+420' },
  { code: 'HU', name: 'Hungary', nameEl: 'Ουγγαρία', dial: '+36' },
  { code: 'HR', name: 'Croatia', nameEl: 'Κροατία', dial: '+385' },
  { code: 'AL', name: 'Albania', nameEl: 'Αλβανία', dial: '+355' },
  { code: 'AU', name: 'Australia', nameEl: 'Αυστραλία', dial: '+61' },
  { code: 'CA', name: 'Canada', nameEl: 'Καναδάς', dial: '+1' },
];

const Booking = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const el = i18n.language === 'el';

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  // Form state
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [numGuests, setNumGuests] = useState(2);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestCountry, setGuestCountry] = useState('');
  const [paymentType, setPaymentType] = useState('full');
  const [specialRequests, setSpecialRequests] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Price
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);

  // Deposit inquiry result
  const [depositResult, setDepositResult] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propRes, datesRes] = await Promise.all([
          axios.get(`${API}/properties/${slug}`),
          axios.get(`${API}/bookings/dates/${slug}`),
        ]);
        setProperty(propRes.data);
        setBookedDates(datesRes.data.booked_dates || []);
      } catch (error) {
        console.error('Error fetching property:', error);
        toast.error(el ? 'Αδυναμία φόρτωσης καταλύματος' : 'Failed to load property details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, el]);

  const calculatePrice = useCallback(async () => {
    if (!checkInDate || !checkOutDate || !property) return;
    setCalculatingPrice(true);
    try {
      const res = await axios.post(`${API}/pricing/calculate`, {
        property_id: property.id,
        check_in_date: format(checkInDate, 'yyyy-MM-dd'),
        check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
        num_guests: numGuests,
      });
      setPriceBreakdown(res.data);
    } catch {
      toast.error(el ? 'Αδυναμία υπολογισμού τιμής' : 'Failed to calculate price');
    } finally {
      setCalculatingPrice(false);
    }
  }, [checkInDate, checkOutDate, numGuests, property, el]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  const isDateBooked = (d) => bookedDates.includes(format(d, 'yyyy-MM-dd'));

  const handleCountryChange = (code) => {
    setGuestCountry(code);
    const country = COUNTRIES.find(c => c.code === code);
    if (country) {
      const currentPhone = guestPhone.replace(/^\+\d+\s?/, '');
      setGuestPhone(country.dial + ' ' + currentPhone);
    }
  };

  const handleDateSelect = (date) => {
    if (!checkInDate || (checkInDate && checkOutDate)) {
      setCheckInDate(date);
      setCheckOutDate(null);
      setPriceBreakdown(null);
    } else if (checkInDate && !checkOutDate) {
      if (date > checkInDate) {
        setCheckOutDate(date);
      } else {
        toast.error(el ? 'Η αναχώρηση πρέπει να είναι μετά την άφιξη' : 'Check-out must be after check-in');
      }
    }
  };

  /* ---- SUBMIT: full payment → Stripe, deposit → inquiry ---- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkInDate || !checkOutDate) {
      toast.error(el ? 'Επιλέξτε ημερομηνίες' : 'Please select dates');
      return;
    }
    if (!guestName || !guestEmail || !guestPhone) {
      toast.error(el ? 'Συμπληρώστε τα στοιχεία σας' : 'Please fill in all guest information');
      return;
    }
    if (!guestCountry) {
      toast.error(el ? 'Επιλέξτε χώρα' : 'Please select your country');
      return;
    }
    const nights = differenceInDays(checkOutDate, checkInDate);
    if (nights < property.minimum_stay_days) {
      toast.error(el
        ? `Ελάχιστη διαμονή ${property.minimum_stay_days} διανυκτερεύσεις`
        : `Minimum stay is ${property.minimum_stay_days} nights`);
      return;
    }
    if (!acceptedTerms) {
      toast.error(el ? 'Πρέπει να αποδεχτείτε τους όρους κράτησης' : 'Please accept the cancellation policy and terms');
      return;
    }

    setSubmitting(true);

    if (paymentType === 'full') {
      /* --- FULL PAYMENT → Stripe --- */
      try {
        const bookingRes = await axios.post(`${API}/bookings/create`, {
          property_id: property.id,
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone,
          check_in_date: format(checkInDate, 'yyyy-MM-dd'),
          check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
          num_guests: numGuests,
          payment_type: 'full',
          guest_country: guestCountry,
          special_requests: specialRequests,
        });
        const origin = window.location.origin;
        const checkoutRes = await axios.post(`${API}/payments/checkout/session`, {
          booking_id: bookingRes.data.booking_id,
          success_url: `${origin}/booking-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/booking/${slug}`,
        });
        window.location.href = checkoutRes.data.checkout_url;
      } catch (error) {
        toast.error(error.response?.data?.detail || (el ? 'Σφάλμα κράτησης' : 'Booking error'));
        setSubmitting(false);
      }
    } else {
      /* --- DEPOSIT → Inquiry (no Stripe) --- */
      try {
        const res = await axios.post(`${API}/bookings/deposit-inquiry`, {
          property_id: property.id,
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone,
          check_in_date: format(checkInDate, 'yyyy-MM-dd'),
          check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
          num_guests: numGuests,
          preferred_contact: 'whatsapp',
          guest_country: guestCountry,
          message: specialRequests,
          language: i18n.language,
        });
        setDepositResult(res.data);
        toast.success(el ? 'Το αίτημά σας καταχωρήθηκε!' : 'Your inquiry has been submitted!');
      } catch (error) {
        toast.error(error.response?.data?.detail || (el ? 'Σφάλμα αιτήματος' : 'Inquiry error'));
      } finally {
        setSubmitting(false);
      }
    }
  };

  /* ---- DEPOSIT SUCCESS VIEW ---- */
  if (depositResult) {
    const ci = depositResult.contact_info || {};
    return (
      <div className="bg-stone-50 min-h-screen py-12" data-testid="deposit-inquiry-success">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-white rounded-sm border border-stone-200 p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Landmark className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold mb-2" data-testid="deposit-success-title">
              {el ? 'Το Αίτημά σας Καταχωρήθηκε' : 'Inquiry Submitted'}
            </h1>
            <p className="text-stone-600 mb-6">
              {el
                ? 'Θα επικοινωνήσουμε σύντομα μαζί σας για να ολοκληρώσουμε την κράτηση με προκαταβολή 35%.'
                : 'We will contact you shortly to arrange the 35% deposit via bank transfer.'}
            </p>

            <div className="bg-stone-50 rounded-sm p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-stone-500">{el ? 'Σύνολο' : 'Total'}</span>
                <span className="font-semibold">&euro;{depositResult.total_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">{el ? 'Προκαταβολή 35%' : '35% Deposit'}</span>
                <span className="font-semibold text-primary">&euro;{depositResult.deposit_amount?.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-sm text-stone-500 mb-4">
              {el ? 'Μπορείτε επίσης να επικοινωνήσετε απευθείας:' : 'You can also contact us directly:'}
            </p>
            <div className="space-y-3">
              <a href={ci.whatsapp} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 text-white rounded-sm hover:bg-green-600 transition-colors font-medium"
                data-testid="contact-whatsapp">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
              <a href={`tel:${ci.phone}`}
                className="flex items-center justify-center gap-2 w-full py-3 border border-stone-300 text-stone-700 rounded-sm hover:bg-stone-50 transition-colors"
                data-testid="contact-phone">
                <Phone className="w-5 h-5" /> {ci.phone}
              </a>
              <a href={`mailto:${ci.email}`}
                className="flex items-center justify-center gap-2 w-full py-3 border border-stone-300 text-stone-700 rounded-sm hover:bg-stone-50 transition-colors"
                data-testid="contact-email">
                <Mail className="w-5 h-5" /> {ci.email}
              </a>
            </div>

            <button onClick={() => navigate('/')} className="mt-6 text-primary hover:underline text-sm" data-testid="back-home-link">
              {el ? 'Επιστροφή στην Αρχική' : 'Back to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- LOADING / NOT FOUND ---- */
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center">
        <div>
          <h2 className="text-2xl mb-4">{el ? 'Το κατάλυμα δεν βρέθηκε' : 'Property not found'}</h2>
          <button onClick={() => navigate('/properties')} className="text-primary hover:underline">
            {el ? 'Επιστροφή στα Καταλύματα' : 'Back to Properties'}
          </button>
        </div>
      </div>
    );
  }

  /* ---- MAIN BOOKING FORM ---- */
  return (
    <div className="bg-stone-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-semibold mb-2">{t('booking.title')}</h1>
          <p className="text-lg text-stone-600 font-light">{property.name}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">

              {/* DATES */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-primary" />
                  {t('booking.selectDates')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{t('booking.checkIn')}</label>
                    <div className="p-3 bg-stone-50 rounded-sm border border-stone-200" data-testid="checkin-display">
                      {checkInDate ? format(checkInDate, 'PPP') : (el ? 'Επιλέξτε ημερομηνία' : 'Select date')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{t('booking.checkOut')}</label>
                    <div className="p-3 bg-stone-50 rounded-sm border border-stone-200" data-testid="checkout-display">
                      {checkOutDate ? format(checkOutDate, 'PPP') : (el ? 'Επιλέξτε ημερομηνία' : 'Select date')}
                    </div>
                  </div>
                </div>
                <CalendarComponent
                  mode="single"
                  selected={checkInDate}
                  onSelect={handleDateSelect}
                  disabled={(d) => d < new Date() || isDateBooked(d)}
                  className="rounded-sm border border-stone-200"
                />
              </div>

              {/* GUESTS */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-primary" />
                  {t('booking.guests')}
                </h2>
                <input
                  type="number" min="1" max={property.max_guests} value={numGuests}
                  onChange={(e) => setNumGuests(parseInt(e.target.value) || 1)}
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="num-guests-input"
                />
                <p className="text-xs text-stone-500 mt-2">
                  {el ? `Μέγιστο ${property.max_guests} άτομα` : `Maximum ${property.max_guests} guests`}
                </p>
              </div>

              {/* GUEST INFO */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <h2 className="text-xl font-semibold mb-4">{t('booking.guestInfo')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{t('booking.name')} *</label>
                    <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)}
                      className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" required data-testid="guest-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{t('booking.email')} *</label>
                    <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
                      className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" required data-testid="guest-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{el ? 'Χώρα' : 'Country'} *</label>
                    <select
                      value={guestCountry}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      required
                      data-testid="guest-country-select"
                    >
                      <option value="">{el ? 'Επιλέξτε χώρα...' : 'Select country...'}</option>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{el ? c.nameEl : c.name} ({c.dial})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">{t('booking.phone')} *</label>
                    <input type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)}
                      className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" required data-testid="guest-phone-input" />
                  </div>
                </div>
              </div>

              {/* PAYMENT TYPE */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-primary" />
                  {t('booking.paymentType')}
                </h2>
                <div className="space-y-3">
                  {/* Full payment via Stripe */}
                  <label className={`flex items-start p-4 border-2 rounded-sm cursor-pointer transition-colors ${paymentType === 'full' ? 'border-primary bg-primary/5' : 'border-stone-200 hover:border-primary'}`}>
                    <input type="radio" name="paymentType" value="full" checked={paymentType === 'full'}
                      onChange={(e) => setPaymentType(e.target.value)} className="mt-1 mr-3" data-testid="payment-full" />
                    <div>
                      <p className="font-semibold text-stone-900">{t('booking.payFull')}</p>
                      <p className="text-sm text-stone-600 mt-1">
                        {el ? 'Πληρώστε το πλήρες ποσό τώρα με κάρτα. Χωρίς επιπλέον χρεώσεις.' : 'Pay the full amount now by card. No additional charges.'}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        <CreditCard className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-400">{el ? 'Ασφαλής πληρωμή μέσω Stripe' : 'Secure payment via Stripe'}</span>
                      </div>
                    </div>
                  </label>

                  {/* Deposit via bank transfer / contact */}
                  <label className={`flex items-start p-4 border-2 rounded-sm cursor-pointer transition-colors ${paymentType === 'deposit' ? 'border-primary bg-primary/5' : 'border-stone-200 hover:border-primary'}`}>
                    <input type="radio" name="paymentType" value="deposit" checked={paymentType === 'deposit'}
                      onChange={(e) => setPaymentType(e.target.value)} className="mt-1 mr-3" data-testid="payment-deposit" />
                    <div>
                      <p className="font-semibold text-stone-900">{t('booking.payDeposit')}</p>
                      <p className="text-sm text-stone-600 mt-1">
                        {el
                          ? 'Προκαταβολή 35% μέσω τραπεζικής μεταφοράς. Θα επικοινωνήσουμε μαζί σας για τα στοιχεία.'
                          : '35% deposit via bank transfer. We will contact you to arrange the details.'}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="flex items-center gap-1 text-xs text-stone-400"><Phone className="w-3.5 h-3.5" /> {el ? 'Τηλέφωνο' : 'Phone'}</span>
                        <span className="flex items-center gap-1 text-xs text-stone-400"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</span>
                        <span className="flex items-center gap-1 text-xs text-stone-400"><Mail className="w-3.5 h-3.5" /> Email</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* SPECIAL REQUESTS */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <h2 className="text-xl font-semibold mb-4">{t('booking.specialRequests')}</h2>
                <textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={3}
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={el ? 'Τυχόν ερωτήσεις ή αιτήματα;' : 'Any special requests or questions?'}
                  data-testid="special-requests" />
              </div>

              {/* TERMS & CONDITIONS */}
              <div className="bg-white p-6 rounded-sm border border-stone-200">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-primary shrink-0"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-sm text-stone-700">
                    {el
                      ? 'Αποδέχομαι την πολιτική ακύρωσης και τους όρους κράτησης'
                      : 'I accept the cancellation policy and terms of booking'}
                  </span>
                </label>
                <div className="mt-3 ml-7 text-xs text-stone-500 leading-relaxed">
                  {el ? (
                    <>Δωρεάν ακύρωση εάν γίνει περισσότερες από 21 ημέρες πριν την άφιξη. Ακυρώσεις εντός 21 ημερών πριν την άφιξη δεν επιστρέφονται. Για περισσότερες πληροφορίες, δείτε την <a href="/policies" target="_blank" rel="noopener noreferrer" className="text-primary underline">πολιτική ακύρωσης</a>.</>
                  ) : (
                    <>Free cancellation if made more than 21 days before check-in. Cancellations within 21 days of check-in are non-refundable. For more details, see our <a href="/policies" target="_blank" rel="noopener noreferrer" className="text-primary underline">cancellation policy</a>.</>
                  )}
                </div>
              </div>
            </div>

            {/* PRICE SIDEBAR */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-sm border border-stone-200 sticky top-24">
                <h2 className="text-xl font-semibold mb-6">{t('booking.priceBreakdown')}</h2>

                {calculatingPrice ? (
                  <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></div>
                ) : priceBreakdown ? (
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">{el ? 'Βασική Τιμή' : 'Base Price'} ({priceBreakdown.num_nights} {el ? 'διαν.' : 'nights'})</span>
                      <span className="font-semibold">&euro;{priceBreakdown.base_price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">{t('property.cleaningFee')}</span>
                      <span className="font-semibold">&euro;{priceBreakdown.cleaning_fee.toFixed(2)}</span>
                    </div>
                    {priceBreakdown.extra_person_fee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-600">{t('property.extraPerson')}</span>
                        <span className="font-semibold">&euro;{priceBreakdown.extra_person_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">{t('property.occupancyTax')}</span>
                      <span className="font-semibold">&euro;{priceBreakdown.occupancy_tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-4 flex justify-between">
                      <span className="font-semibold text-lg">{el ? 'Σύνολο' : 'Total'}</span>
                      <span className="font-semibold text-2xl text-primary">&euro;{priceBreakdown.total_price.toFixed(2)}</span>
                    </div>

                    {paymentType === 'deposit' && (
                      <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
                        <p className="text-sm font-semibold text-amber-800 mb-1">
                          {el ? 'Προκαταβολή 35%:' : '35% Deposit:'}
                        </p>
                        <p className="text-2xl font-semibold text-amber-800">
                          &euro;{(priceBreakdown.total_price * 0.35).toFixed(2)}
                        </p>
                        <p className="text-xs text-amber-700 mt-2">
                          {el
                            ? 'Μέσω τραπεζικής μεταφοράς — θα επικοινωνήσουμε μαζί σας.'
                            : 'Via bank transfer — we will contact you to arrange.'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 text-center py-8">
                    {el ? 'Επιλέξτε ημερομηνίες για να δείτε τις τιμές' : 'Select dates to see price breakdown'}
                  </p>
                )}

                <button type="submit" disabled={!priceBreakdown || submitting}
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white py-4 rounded-sm transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  data-testid="proceed-payment-button">
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> {el ? 'Επεξεργασία...' : 'Processing...'}</>
                  ) : paymentType === 'full' ? (
                    el ? 'Συνέχεια στην Πληρωμή' : 'Proceed to Payment'
                  ) : (
                    el ? 'Υποβολή Αιτήματος Κράτησης' : 'Submit Booking Inquiry'
                  )}
                </button>

                <p className="text-xs text-center text-stone-500 mt-4">
                  {paymentType === 'full'
                    ? (el ? 'Ασφαλής πληρωμή μέσω Stripe. Χωρίς επιπλέον χρεώσεις.' : 'Secure payment via Stripe. No additional fees.')
                    : (el ? 'Χωρίς online πληρωμή — θα επικοινωνήσουμε μαζί σας.' : 'No online payment — we will contact you to arrange.')}
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Booking;
